import React, { useEffect, useState } from "react";
import { BarChart3, Download } from "lucide-react";
import { api } from "../utils/api";
import { downloadCsv } from "../utils/csv";

const PERIODS = [
  { key: "daily", label: "Daily (24h)" },
  { key: "monthly", label: "Monthly (30d)" },
  { key: "yearly", label: "Yearly (365d)" },
];

const TICKET_TYPES = [
  { key: "incidents", label: "Incidents" },
  { key: "service_requests", label: "Service Requests" },
  { key: "change_requests", label: "Change Requests" },
  { key: "problems", label: "Problems" },
];

function Bars({ data }) {
  const max = Math.max(1, ...Object.values(data || {}));
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <div className="text-sm text-gray-500">No data</div>;
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-xs">
          <div className="w-32 truncate">{k}</div>
          <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
            <div className="bg-brand-500 h-full" style={{ width: `${(v / max) * 100}%` }} />
          </div>
          <div className="w-10 text-right tabular-nums">{v}</div>
        </div>
      ))}
    </div>
  );
}

function KPISection({ title, kpi }) {
  return (
    <div className="card p-5">
      <h3 className="font-semibold text-gray-800 mb-3">{title}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <Stat label="Total" value={kpi.total} />
        <Stat label="Closed" value={kpi.closed} />
        <Stat label="Avg resolution (h)" value={kpi.avg_resolution_hours} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold text-gray-600 mb-1">By status</div>
          <Bars data={kpi.by_status} />
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-600 mb-1">By plant</div>
          <Bars data={kpi.by_plant} />
        </div>
        {Object.keys(kpi.by_priority || {}).length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-600 mb-1">By priority</div>
            <Bars data={kpi.by_priority} />
          </div>
        )}
        {Object.keys(kpi.by_category || {}).length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-600 mb-1">By category</div>
            <Bars data={kpi.by_category} />
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-gray-50 rounded p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-2xl font-bold text-gray-800 tabular-nums">{value ?? 0}</div>
    </div>
  );
}

export default function Reports() {
  const [period, setPeriod] = useState("monthly");
  const [kpi, setKpi] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [slaCfg, setSlaCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [{ data: k }, { data: o }, { data: c }] = await Promise.all([
        api.get(`/api/dashboard/kpi?period=${period}`),
        api.get(`/api/dashboard/overdue`),
        api.get(`/api/sla/config`),
      ]);
      setKpi(k);
      setOverdue(o);
      setSlaCfg(c);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [period]); // eslint-disable-line

  async function runScan() {
    setScanning(true);
    try {
      await api.post("/api/sla/scan");
      await load();
    } finally { setScanning(false); }
  }

  function exportSummary() {
    if (!kpi) return;
    const rows = TICKET_TYPES.map((t) => ({
      type: t.label,
      total: kpi[t.key]?.total ?? 0,
      closed: kpi[t.key]?.closed ?? 0,
      avg_resolution_hours: kpi[t.key]?.avg_resolution_hours ?? 0,
    }));
    downloadCsv(`itsm-kpi-${period}-${new Date().toISOString().slice(0, 10)}.csv`, rows, ["type", "total", "closed", "avg_resolution_hours"]);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="text-brand-500" />
          <h1 className="text-2xl font-bold">Reports</h1>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={runScan} disabled={scanning}>{scanning ? "Scanning…" : "Run SLA scan"}</button>
          <button className="btn-secondary flex items-center gap-2" onClick={exportSummary}><Download size={16} /> CSV summary</button>
        </div>
      </div>

      {slaCfg && (
        <div className="card p-4">
          <h3 className="font-semibold mb-2">SLA configuration in effect</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-xs font-medium text-gray-500">Incidents</div>
              <div>Critical: <b>{slaCfg.incidents.critical}h</b></div>
              <div>High: <b>{slaCfg.incidents.high}h</b></div>
              <div>Medium: <b>{slaCfg.incidents.medium}h</b></div>
              <div>Low: <b>{slaCfg.incidents.low}h</b></div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500">Service Requests</div>
              <div>All: <b>{slaCfg.service_requests.all}h</b></div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500">Change Requests</div>
              <div>Emergency: <b>{slaCfg.change_requests.emergency}h</b></div>
              <div>Standard: <b>{slaCfg.change_requests.standard}h</b></div>
              <div>Normal: <b>{slaCfg.change_requests.normal}h</b></div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500">Monitor</div>
              <div>Warn at: <b>{slaCfg.warning_threshold_pct}%</b></div>
              <div>Scan interval: <b>{Math.round(slaCfg.scan_interval_seconds / 60)} min</b></div>
              <div>Auto-close SR after: <b>{slaCfg.auto_close_after_days} days</b></div>
            </div>
          </div>
        </div>
      )}

      <div className="card p-3 flex gap-2">
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)}
            className={`px-3 py-1.5 rounded text-sm font-medium ${period === p.key ? "bg-brand-500 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800"}`}>
            {p.label}
          </button>
        ))}
        {kpi && <span className="text-xs text-gray-500 ml-auto self-center">Since {String(kpi.since).slice(0, 16).replace("T", " ")} UTC</span>}
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}

      {kpi && TICKET_TYPES.map((t) => (
        <KPISection key={t.key} title={t.label} kpi={kpi[t.key] || {}} />
      ))}

      {overdue?.groups?.length > 0 && (
        <div className="card p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Overdue work — by assignee</h3>
          <table className="w-full text-sm">
            <thead className="text-left text-gray-600 text-xs">
              <tr><th className="p-2">Assigned to</th><th className="p-2">Type</th><th className="p-2">Number</th><th className="p-2">Title</th><th className="p-2">Status</th><th className="p-2">Plant</th><th className="p-2">Created</th><th className="p-2">Deadline</th></tr>
            </thead>
            <tbody>
              {overdue.groups.flatMap((g) => g.items.map((it) => (
                <tr key={`${g.assignee}-${it.type}-${it.id}`} className="border-t">
                  <td className="p-2">{g.assignee}</td>
                  <td className="p-2 text-xs">{it.type.replace("_", " ")}</td>
                  <td className="p-2 font-mono text-xs">{it.number}</td>
                  <td className="p-2">{it.title}</td>
                  <td className="p-2"><span className="badge bg-gray-100 text-gray-700">{it.status}</span></td>
                  <td className="p-2">{it.plant}</td>
                  <td className="p-2 text-xs text-gray-500">{it.created_at && new Date(it.created_at).toLocaleDateString()}</td>
                  <td className="p-2 text-xs text-red-600">{it.deadline && new Date(it.deadline).toLocaleString()}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
